import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Chip,
  CircularProgress, Alert, Grid, AppBar, Toolbar, Button,
  TextField, InputAdornment
} from '@mui/material';
import {
  MonitorHeart, Download as DownloadIcon,
  LockOutlined as LockIcon, Phone as PhoneIcon,
  FavoriteOutlined as HeartIcon,
} from '@mui/icons-material';
import { reportAPI } from '../services/api';
import { generateReport } from '../utils/generateReport';
import toast from 'react-hot-toast';

const BLUE = '#00A8E8';
const DARK_BLUE = '#005B8F';
const GREEN = '#86E000';
const RESEND_SECONDS = 30;

function VitalChip({ label, value, unit, alert }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 2, border: `1px solid ${alert ? '#fee2e2' : '#e0f2fe'}`,
      bgcolor: alert ? '#fff7f7' : '#f0f9ff', textAlign: 'center'
    }}>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, color: alert ? '#dc2626' : DARK_BLUE }}>
        {value ?? '—'}
      </Typography>
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>{unit}</Typography>
    </Box>
  );
}

function OtpGate({ code, maskedMobile, expiresAt, onVerified }) {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  function startResendTimer() {
    setResendTimer(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  const handleSendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await reportAPI.sendShortLinkOtp(code);
      setOtpSent(true);
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setError('');
    setLoading(true);
    try {
      const { data: resp } = await reportAPI.verifyShortLinkOtp(code, otp);
      onVerified(resp.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError('');
    setOtp('');
    setLoading(true);
    try {
      await reportAPI.sendShortLinkOtp(code);
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f9ff', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0} sx={{ background: `linear-gradient(135deg, ${DARK_BLUE}, ${BLUE})` }}>
        <Toolbar>
          <MonitorHeart sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Eros Wellness</Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 4, boxShadow: '0 8px 40px rgba(0,91,143,0.12)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>

            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '18px', mx: 'auto', mb: 2,
                background: `linear-gradient(135deg, ${GREEN}, ${BLUE})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 24px rgba(0,168,232,0.3)`,
              }}>
                {otpSent ? <LockIcon sx={{ color: '#fff', fontSize: 30 }} /> : <HeartIcon sx={{ color: '#fff', fontSize: 30 }} />}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontFamily: 'Montserrat, sans-serif' }}>
                {otpSent ? 'Enter OTP' : 'Verify Identity'}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.88rem', mt: 0.5 }}>
                {otpSent
                  ? <>OTP sent to <strong>+91 {maskedMobile}</strong>. Valid for 5 minutes.</>
                  : <>Your health report is ready. Verify with OTP sent to <strong>+91 {maskedMobile}</strong>.</>
                }
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

            {!otpSent ? (
              <Button
                fullWidth variant="contained" size="large"
                onClick={handleSendOtp}
                disabled={loading}
                sx={{
                  py: 1.6, fontSize: '1rem', borderRadius: 3, fontWeight: 700,
                  background: `linear-gradient(135deg, ${GREEN} 0%, ${BLUE} 100%)`,
                  color: '#fff',
                  boxShadow: `0 6px 20px rgba(0,168,232,0.3)`,
                  '&:hover': { boxShadow: `0 8px 28px rgba(0,168,232,0.4)`, transform: 'translateY(-1px)' },
                  '&:disabled': { opacity: 0.6 },
                  transition: 'all 0.2s ease',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP to My Mobile'}
              </Button>
            ) : (
              <form onSubmit={handleVerify}>
                <TextField
                  fullWidth label="6-Digit OTP" value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  required disabled={loading}
                  placeholder="Enter 6-digit OTP"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: GREEN }} />
                      </InputAdornment>
                    )
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
                  <Button
                    size="small" variant="text"
                    onClick={handleResend}
                    disabled={resendTimer > 0 || loading}
                    sx={{ color: resendTimer > 0 ? '#94a3b8' : BLUE, fontSize: '0.8rem', p: 0, textTransform: 'none' }}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                  </Button>
                </Box>

                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading || otp.length !== 6}
                  sx={{
                    py: 1.6, fontSize: '1rem', borderRadius: 3, fontWeight: 700,
                    background: `linear-gradient(135deg, ${GREEN} 0%, ${BLUE} 100%)`,
                    color: '#fff',
                    boxShadow: `0 6px 20px rgba(0,168,232,0.3)`,
                    '&:hover': { boxShadow: `0 8px 28px rgba(0,168,232,0.4)`, transform: 'translateY(-1px)' },
                    '&:disabled': { opacity: 0.6 },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'View My Report'}
                </Button>
              </form>
            )}

            {expiresAt && (
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', textAlign: 'center', mt: 2.5 }}>
                Link expires {new Date(expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default function ReportViewer() {
  const { code } = useParams();
  const [step, setStep] = useState('loading'); // loading | otp | report | error
  const [linkInfo, setLinkInfo] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    reportAPI.getShortCodeInfo(code)
      .then(({ data: res }) => {
        setLinkInfo(res);
        setStep('otp');
      })
      .catch(err => {
        const msg = err.response?.data?.message || 'Report link is invalid or has expired.';
        setError(msg);
        setStep('error');
      });
  }, [code]);

  const handleVerified = (reportData) => {
    setData(reportData);
    setStep('report');
  };

  const handleDownload = (report, patientInfo) => {
    try {
      const td = report.testData || {};
      generateReport({
        patient: patientInfo,
        session: report._rawSession || null,
        testData: report._rawTestData || td,
        legacyVitals: report._rawLegacy || null,
        reportGeneratedAt: new Date().toISOString(),
        maskPii: false,
      });
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  if (step === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f9ff' }}>
        <CircularProgress sx={{ color: BLUE }} />
      </Box>
    );
  }

  if (step === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f9ff', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>{error}</Alert>
      </Box>
    );
  }

  if (step === 'otp') {
    return (
      <OtpGate
        code={code}
        maskedMobile={linkInfo?.maskedMobile}
        expiresAt={linkInfo?.expiresAt}
        onVerified={handleVerified}
      />
    );
  }

  // step === 'report'
  const patient = data?.patient || {};
  const allReports = [
    ...(data?.reports || []).map(s => {
      const td = s.testData || {};
      return {
        ...s,
        testDate: s.startedAt || s.completedAt,
        _rawTestData: td,
        _rawSession: { sessionId: s.sessionId, status: s.status, location: s.location, startedAt: s.startedAt, completedAt: s.completedAt, totalStations: s.totalStations, completedStations: s.completedStations },
        vitals: {
          systolic: td.bloodPressure?.systolic, diastolic: td.bloodPressure?.diastolic,
          heartRate: td.heartRate?.value, oxygenSaturation: td.oxygenSaturation?.value,
          bodyTemperature: td.bodyTemperature?.value, weight: td.weight?.value,
          height: td.height?.value, bmi: td.bmi?.value,
          bloodGlucoseFasting: td.bloodGlucoseFasting?.value, hemoglobin: td.hemoglobin?.value,
        }
      };
    }),
    ...(data?.legacyReports || []).map(v => ({
      sessionId: v._id, testDate: v.testDate || v.createdAt, status: 'completed',
      _rawLegacy: v,
      vitals: {
        systolic: v.bloodPressure?.systolic, diastolic: v.bloodPressure?.diastolic,
        heartRate: v.heartRate?.value, oxygenSaturation: v.oxygenSaturation?.value,
        bodyTemperature: v.bodyTemperature?.value, weight: v.weight?.value,
        height: v.height?.value, bmi: v.bmi?.value,
        hemoglobin: v.hemoglobin?.value,
      }
    }))
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f9ff' }}>
      <AppBar position="sticky" elevation={0} sx={{ background: `linear-gradient(135deg, ${DARK_BLUE}, ${BLUE})` }}>
        <Toolbar>
          <MonitorHeart sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Eros Wellness</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Link expires: {data?.expiresAt ? new Date(data.expiresAt).toLocaleDateString('en-IN') : ''}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Patient Info */}
        <Card sx={{ mb: 3, borderRadius: 3, background: `linear-gradient(135deg, ${DARK_BLUE}, ${BLUE})`, color: '#fff' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {[patient.title, patient.name].filter(Boolean).join(' ') || 'Patient'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {patient.gender && <Chip label={patient.gender} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
              {patient.age && <Chip label={`Age ${patient.age}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
              {patient.mobile && <Chip label={patient.mobile} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
              {patient.location?.name && <Chip label={patient.location.name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, display: 'block' }}>
              {allReports.length} report{allReports.length !== 1 ? 's' : ''} on record
            </Typography>
          </CardContent>
        </Card>

        {allReports.length === 0 ? (
          <Alert severity="info">No reports found for this patient.</Alert>
        ) : (
          allReports.map((report, i) => (
            <Card key={report.sessionId || i} sx={{ mb: 2, borderRadius: 3, border: i === 0 ? `2px solid ${BLUE}` : '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    {i === 0 && <Chip label="Latest" size="small" sx={{ bgcolor: BLUE, color: '#fff', mr: 1, fontWeight: 700 }} />}
                    <Typography variant="body2" sx={{ color: '#64748b', display: 'inline' }}>
                      {report.testDate ? new Date(report.testDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date unknown'}
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(report, patient)}
                    sx={{ color: DARK_BLUE, borderColor: DARK_BLUE, border: '1px solid', borderRadius: 2 }}>
                    Download PDF
                  </Button>
                </Box>

                <Grid container spacing={1.5}>
                  {report.vitals?.systolic && <Grid item xs={6} sm={4} md={3}><VitalChip label="Blood Pressure" value={`${report.vitals.systolic}/${report.vitals.diastolic}`} unit="mmHg" alert={report.vitals.systolic > 140} /></Grid>}
                  {report.vitals?.heartRate && <Grid item xs={6} sm={4} md={3}><VitalChip label="Heart Rate" value={report.vitals.heartRate} unit="bpm" alert={report.vitals.heartRate > 100 || report.vitals.heartRate < 60} /></Grid>}
                  {report.vitals?.oxygenSaturation && <Grid item xs={6} sm={4} md={3}><VitalChip label="SpO2" value={report.vitals.oxygenSaturation} unit="%" alert={report.vitals.oxygenSaturation < 95} /></Grid>}
                  {report.vitals?.bodyTemperature && <Grid item xs={6} sm={4} md={3}><VitalChip label="Temperature" value={report.vitals.bodyTemperature} unit="°F" alert={report.vitals.bodyTemperature > 99.5} /></Grid>}
                  {report.vitals?.weight && <Grid item xs={6} sm={4} md={3}><VitalChip label="Weight" value={report.vitals.weight} unit="kg" /></Grid>}
                  {report.vitals?.bmi && <Grid item xs={6} sm={4} md={3}><VitalChip label="BMI" value={report.vitals.bmi} unit="kg/m²" alert={report.vitals.bmi > 25} /></Grid>}
                  {report.vitals?.hemoglobin && <Grid item xs={6} sm={4} md={3}><VitalChip label="Hemoglobin" value={report.vitals.hemoglobin} unit="g/dL" alert={report.vitals.hemoglobin < 12} /></Grid>}
                  {report.vitals?.bloodGlucoseFasting && <Grid item xs={6} sm={4} md={3}><VitalChip label="Glucose (F)" value={report.vitals.bloodGlucoseFasting} unit="mg/dL" alert={report.vitals.bloodGlucoseFasting > 100} /></Grid>}
                </Grid>

                {report.recommendation?.text && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderLeft: '4px solid #16a34a', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700 }}>Doctor's Recommendation</Typography>
                    <Typography variant="body2" sx={{ color: '#166534', mt: 0.5 }}>{report.recommendation.text}</Typography>
                    <Typography variant="caption" sx={{ color: '#4ade80' }}>
                      — Dr. {report.recommendation.doctorName}{report.recommendation.doctorDesignation ? `, ${report.recommendation.doctorDesignation}` : ''}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        )}

        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', textAlign: 'center', mt: 2 }}>
          This link expires on {data?.expiresAt ? new Date(data.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}. Do not share with others.
        </Typography>
      </Box>
    </Box>
  );
}
